import React, { useState, useEffect } from "react";
import Button from "@scripts/react/components/Button";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { projectSelector } from "@rflocal/shared";

interface EmbeddedWorkflowActionsProps {
    onDownloadClick: () => void;
    onShareClick: () => void;
    isDownloading: boolean;
    shareUrl: string | null;
    copied: boolean;
    isMobile?: boolean;
}

export const EmbeddedWorkflowActions: React.FC<EmbeddedWorkflowActionsProps> = ({
    onDownloadClick,
    onShareClick,
    isDownloading,
    shareUrl,
    copied,
    isMobile = false
}) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const [showTooltips, setShowTooltips] = useState(false);

    // Responsive breakpoint detection - show tooltips only on md screens (768px-1023px)
    useEffect(() => {
        const mdQuery = window.matchMedia("(min-width: 768px)"); // md breakpoint
        const lgQuery = window.matchMedia("(min-width: 1024px)"); // lg breakpoint

        const handleScreenChange = () => {
            // Show tooltips only on md screens (768px-1023px), not on lg+ (1024px+)
            setShowTooltips(mdQuery.matches && !lgQuery.matches);
        };

        // Set initial state
        handleScreenChange();

        // Listen for changes
        mdQuery.addEventListener("change", handleScreenChange);
        lgQuery.addEventListener("change", handleScreenChange);

        return () => {
            mdQuery.removeEventListener("change", handleScreenChange);
            lgQuery.removeEventListener("change", handleScreenChange);
        };
    }, []);

    return (
        <div className="flex items-center gap-2">
            {!isMobile && (
                <TooltipContainer
                    type="titleOnly"
                    title="Build Your Own Model"
                    placement="top"
                    disabled={!showTooltips}
                    customButton={
                        <Button
                            type="secondary"
                            size="small"
                            className="md:btn-ghost lg:btn-secondary"
                            onClick={() => {
                                const workspaceUrl = currentWorkspace?.url
                                    ? `/${currentWorkspace.url}`
                                    : "";
                                const rapidUrl = `${projectSelector.rapidUrl(false, true)}${workspaceUrl}`;
                                window.open(rapidUrl, "_blank");
                            }}
                        >
                            <i className="far fa-chart-network" />
                            <span className="hidden lg:inline">Build My Own Model</span>
                        </Button>
                    }
                />
            )}
            <TooltipContainer
                type="titleOnly"
                title="Download File"
                placement="top"
                disabled={!showTooltips}
                customButton={
                    <Button
                        type="secondary"
                        size={isMobile ? "xlarge" : "small"}
                        shape={isMobile ? "circle" : undefined}
                        className={isMobile ? "!border-gray-200" : undefined}
                        onClick={onDownloadClick}
                        disabled={isDownloading}
                    >
                        <i
                            className={isDownloading ? "far fa-spinner fa-spin" : "far fa-download"}
                        />
                        {!isMobile && (
                            <span className="hidden lg:inline">
                                {isDownloading ? "Downloading..." : "Download File"}
                            </span>
                        )}
                    </Button>
                }
            />
            <TooltipContainer
                type="titleOnly"
                title="Share Model"
                placement="top"
                disabled={!showTooltips}
                customButton={
                    <Button
                        type="secondary"
                        size={isMobile ? "xlarge" : "small"}
                        shape={isMobile ? "circle" : undefined}
                        onClick={onShareClick}
                        disabled={!shareUrl}
                        className={isMobile ? "!border-gray-200" : undefined}
                    >
                        <i className={copied ? "far fa-check" : "far fa-arrow-up-from-square"} />
                        {!isMobile && (
                            <span className="hidden lg:inline">
                                {copied ? "Link Copied!" : "Share Model"}
                            </span>
                        )}
                    </Button>
                }
            />
        </div>
    );
};
